import { useState, useMemo, useContext } from "react";
import { Box } from "@mui/material";
import ComparisonReport from "~src/sections/competitorAnalytics/analytics";
import BrandTabs from "~src/sections/competitorAnalytics/BrandTabs";
import { SearchBar } from "~src/sections/competitorAnalytics/SearchBar";
import CoverSection from "~src/sections/competitorAnalytics/CoverSection";
import {
  getAudienceDemographics,
  getBrandMetrics,
  getBrandTrafficRevenue,
  getFbAds,
  getTrafficSource,
} from "~src/api/competitorAnalytics";
import { TrafficChannelGraph } from "~src/sections/competitorAnalytics/Graphs/TrafficChannelGraph";
import Demographics from "~src/sections/competitorAnalytics/Graphs/Demographics";
import { getCountryTrafficGrowth, getFullBrand } from "~src/api/brands";
import {
  CountryTrafficGrowth,
  FullBrand,
  OverviewCharts,
  TrafficData,
} from "~src/types/brand";
import GeoMap from "~src/sections/competitorAnalytics/Graphs/GeoMap";
import TrendByCompetitors from "~src/sections/competitorAnalytics/TrendByCompetitors";
import { BrandComparisonContext } from "~src/contexts/BrandComparisonContext";
import LoadingScreen from "~src/components/LoadingScreen";
import { logError } from "~src/utils/logger";
export interface GenderDistribution {
  Female: string;
  Male: string;
}

export type LatestTrafficData = {
  Direct: string;
  Referrals: string;
  Search: string;
  Social: string;
  Mail: string;
  Display: string;
};

export interface AgeDistribution {
  "18 - 24": string;
  "25 - 34": string;
  "35 - 44": string;
  "45 - 54": string;
  "55 - 64": string;
  "65+": string;
}

export type AdByCountry = {
  ad_country: string | null;
  ad_count: number;
};

export type MapData = {
  [key: string]: {
    brand_name: string;
    ads_by_country: AdByCountry[];
  };
};

export type CompetitorAds = {
  total_ads: number;
  map_data: MapData;
  top_countries: any[];
};

export default function CompetitorAnalytics() {
  const [brandComparison, setBrandComparison] = useState<FullBrand[]>([]);
  const [trafficChannelData, setTrafficChannelData] =
    useState<(LatestTrafficData | undefined)[]>();

  const { selectedBrands, setSelectedBrands } =
    useContext(BrandComparisonContext) || {};

  const [countryTrafficData, setCountryTrafficData] = useState<
    CountryTrafficGrowth[][]
  >([]);
  const [genderDistributions, setGenderDistributions] = useState<
    GenderDistribution[]
  >([]);
  const [ageDistributions, setAgeDistributions] = useState<AgeDistribution[]>(
    [],
  );
  const [brandTrafficData, setBrandTrafficData] = useState<TrafficData[]>([]);
  const [brandMetricsData, setBrandMetricsData] = useState<OverviewCharts[]>(
    [],
  );
  const [competitorAds, setCompetitorAds] = useState<CompetitorAds>();
  const [loading, setLoading] = useState(false);

  const getBrandIds = useMemo(() => {
    if (selectedBrands) {
      return selectedBrands.map((brand) => brand.id);
    }
  }, [selectedBrands]);

  const handleAnalyseButtonClick = async () => {
    setLoading(true);

    try {
      loadFbAds();
      await Promise.allSettled([
        loadComparisonReport(),
        loadTrafficSource(),
        loadCountryTraffic(),
        loadAudienceDemographic(),
        loadBrandMetrcs(),
        loadBrandTrafficRevenue(),
      ]);
    } catch (error) {
      logError(`Error occurred: ${error}`);
    }

    setLoading(false);
  };

  const loadComparisonReport = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map((brand) =>
        getFullBrand(parseInt(brand.id)),
      );
      const results = await Promise.all(promises);
      setBrandComparison(results);
    }
  };

  const loadTrafficSource = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map((brand) =>
        getTrafficSource(parseInt(brand.id)),
      );
      const results = await Promise.all(promises);
      setTrafficChannelData(results);
    }
  };

  const loadCountryTraffic = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map(async (brand) => {
        const result = await getCountryTrafficGrowth(parseInt(brand.id));
        return result && result.length > 0 ? result : null;
      });

      const results = (await Promise.all(promises)).filter(
        (result): result is CountryTrafficGrowth[] => result !== null,
      );
      setCountryTrafficData(results);
    }
  };

  const loadAudienceDemographic = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map((brand) =>
        getAudienceDemographics(parseInt(brand.id)),
      );
      const results = await Promise.all(promises);
      const genderResults = results.map((result) => result.GenderDistribution);
      const ageResults = results.map((result) => result.AgeDistribution);

      setGenderDistributions(genderResults);
      setAgeDistributions(ageResults);
    }
  };

  const loadBrandTrafficRevenue = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map((brand) =>
        getBrandTrafficRevenue(parseInt(brand.id)),
      );
      const results = await Promise.all(promises);
      setBrandTrafficData(results);
    }
  };
  const loadBrandMetrcs = async () => {
    if (selectedBrands) {
      const promises = selectedBrands.map((brand) =>
        getBrandMetrics(parseInt(brand.id)),
      );
      const results = await Promise.all(promises);
      setBrandMetricsData(results);
    }
  };
  const loadFbAds = async () => {
    if (selectedBrands) {
      const response = await getFbAds(getBrandIds);
      setCompetitorAds(response);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <SearchBar handleAnalyseButtonClick={handleAnalyseButtonClick} />
      {selectedBrands && selectedBrands.length > 0 && (
        <BrandTabs
          handleAnalyseButtonClick={handleAnalyseButtonClick}
          selectedBrands={selectedBrands}
          setSelectedBrands={setSelectedBrands}
        />
      )}
      {!loading && <ComparisonReport brandComparison={brandComparison} />}
      {!loading &&
        brandTrafficData.length > 0 &&
        brandMetricsData.length > 0 && (
          <TrendByCompetitors
            brandTrafficData={brandTrafficData}
            brandMetricsData={brandMetricsData}
            selectedBrands={selectedBrands}
          />
        )}
      {!loading && countryTrafficData && countryTrafficData.length > 0 && (
        <GeoMap
          loading={loading}
          countryTrafficData={countryTrafficData}
          countryAdsData={competitorAds}
          selectedBrands={selectedBrands}
        />
      )}
      {!selectedBrands || (selectedBrands.length <= 0 && <CoverSection />)}
      {!loading &&
        trafficChannelData &&
        selectedBrands &&
        selectedBrands.length > 0 &&
        trafficChannelData.length > 0 && (
          <TrafficChannelGraph
            loading={loading}
            graphData={trafficChannelData}
            selectedBrands={selectedBrands}
          />
        )}
      {!loading &&
        genderDistributions &&
        selectedBrands &&
        genderDistributions.length > 0 &&
        selectedBrands.length > 0 && (
          <Demographics
            loading={loading}
            ageDistributions={ageDistributions}
            genderDistributions={genderDistributions}
            selectedBrands={selectedBrands}
          />
        )}
      {loading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "30vh",
          }}
        >
          <LoadingScreen fullScreen={false} />
        </div>
      )}
    </Box>
  );
}
